import React, { useEffect, useState } from 'react';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { PieChart, Pie, Cell } from 'recharts';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  ChartColorLegend,
  ChartLabelTypography,
  ChartLegendBogItem,
  ChartLegendGridItem,
  ChartNoDataGridItem,
  DashboardChartWrapper,
  ResponsiveContainerStyled,
} from 'pages/DashboardPage/PieCharts/styles';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { colors } from 'theme/colors';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { useTranslations } from 'hooks/useTranslations';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { EDashboardSortType } from 'models/dashboard';
import { getAncillariesTypeData } from 'requests/dashboard';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

interface IAncillariesChart {
  sortType: EDashboardSortType;
  setLoader: React.Dispatch<React.SetStateAction<boolean>>;
}

const AncillariesChart = ({ sortType, setLoader }: IAncillariesChart) => {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const [typeData, setTypeData] = useState<[number, number, number] | undefined>();
  const [totalValue, setTotalValue] = useState(0);
  const [chartData, setChartData] = useState<any>(undefined);
  const { isDesktop, isMobile } = useThemeBreakpoint();
  const [noData, setNoData] = useState(false);
  const [noDataText, setNoDataText] = useState<string>('');
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    (async () => {
      setLoader(true);
      const data = await getAncillariesTypeData(sortType);
      if (data?.chartValues) {
        const chartValues: [number, number, number] = [
          data.chartValues?.[0]?.value || 0,
          data.chartValues?.[1]?.value || 0,
          data.chartValues?.[2]?.value || 0,
        ];
        const empty = chartValues.every((item) => item === 0);
        setNoData(empty);
        setTypeData(chartValues);
        setTotalValue(chartValues.reduce((total, current) => total + current, 0));
        setChartKey((k) => k + 1);
      } else {
        setNoData(true);
      }
      setLoader(false);
    })();
  }, [sortType, setLoader]);

  useEffect(() => {
    switch (sortType) {
      case EDashboardSortType.Day:
        setNoDataText('dashboard.chart.ancillaries.type.noDataToday');
        break;
      case EDashboardSortType.Week:
        setNoDataText('dashboard.chart.ancillaries.type.noData7days');
        break;
      case EDashboardSortType.Month:
        setNoDataText('dashboard.chart.ancillaries.type.noData30days');
        break;
      case EDashboardSortType.Year:
        setNoDataText('dashboard.chart.ancillaries.type.noDataYear');
        break;
      case EDashboardSortType.All:
        setNoDataText('dashboard.chart.ancillaries.type.noDataAll');
        break;
      default:
        setNoDataText('dashboard.chart.ancillaries.legality.noDataAll');
        break;
    }
  }, [sortType]);

  useEffect(() => {
    setChartData(undefined);
    if (typeData?.length) {
      const newData = [
        {
          id: 'success',
          label: 'Success',
          value: Number(((typeData[0] / totalValue) * 100).toFixed(1)),
          color: colors.success400,
        },
        {
          id: 'primary',
          label: 'Primary',
          value: Number(((typeData[1] / totalValue) * 100).toFixed(1)),
          color: colors.primary500,
        },
        {
          id: 'warning',
          label: 'Warning',
          value: Number(((typeData[2] / totalValue) * 100).toFixed(1)),
          color: colors.warning500,
        },
      ];

      setChartData(newData);
    }
  }, [typeData, totalValue]);

  const chartRender = (
    <Grid container sx={{ height: isDesktop ? 'fit-content' : 'auto', flexDirection: !isMobile ? 'row' : 'column-reverse', justifyContent: "space-between" }}>
      <Grid size={{ xs: 12, sm: 5, md: 5, lg: 12, xl: 5 }} sx={{ padding: '10px 10px', justifyContent: "left" }}>
        <ChartLegendGridItem
          container
          size={{ xs: 12 }}
          sx={{ ...(!isMobile ? {} : { justifyContent: 'center' }) }}
        >
          <ChartLegendBogItem>
            <ChartColorLegend color={colors.success500} />
            <ChartLabelTypography variant="body2">
              {t('dashboard.chart.ancillaries.accessories')}
              <Typography variant="subtitle2" sx={{ display: 'inline', paddingLeft: 1.5 }}>
                {typeData?.[0]}
              </Typography>
            </ChartLabelTypography>
          </ChartLegendBogItem>
        </ChartLegendGridItem>

        <ChartLegendGridItem
          container
          size={{ xs: 12 }}
          sx={{ ...(!isMobile ? {} : { justifyContent: 'center' }) }}
        >
          <ChartLegendBogItem>
            <ChartColorLegend color={colors.primary500} />
            <Typography variant="body2">
              {t('dashboard.chart.ancillaries.ammunition')}
              <Typography variant="subtitle2" sx={{ display: 'inline', paddingLeft: 1.5 }}>
                {typeData?.[1]}
              </Typography>
            </Typography>
          </ChartLegendBogItem>
        </ChartLegendGridItem>

        <ChartLegendGridItem
          container
          size={{ xs: 12 }}
          sx={{ ...(!isMobile ? {} : { justifyContent: 'center' }) }}
        >
          <ChartLegendBogItem>
            <ChartColorLegend color={colors.warning500} />
            <Typography variant="body2">
              {t('dashboard.chart.ancillaries.components')}
              <Typography variant="subtitle2" sx={{ display: 'inline', paddingLeft: 1.5 }}>
                {typeData?.[2]}
              </Typography>
            </Typography>
          </ChartLegendBogItem>
        </ChartLegendGridItem>

        <ChartLegendGridItem
          size={{ xs: 12 }}
          sx={{ ...(!isMobile ? {} : { justifyContent: 'center', textAlign: 'center' }) }}
        >
          <Typography variant="body2">
            {t('dashboard.chart.ancillaries.total').toUpperCase()}
            <Typography variant="subtitle2" sx={{ display: 'inline', paddingLeft: 1.5 }}>
              {totalValue}
            </Typography>
          </Typography>
        </ChartLegendGridItem>
      </Grid>
      <Grid
        size={{ xs: 12, sm: 5, md: 5, lg: 12, xl: 5 }}
        sx={{
          ...(!isMobile
            ? { display: 'block', alignSelf: 'center' }
            : { display: 'flex', justifyContent: 'center' }),
        }}
      >
        {chartData && (
          <ResponsiveContainerStyled width="100%" height={isDesktop ? 220 : 200}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="label"
                labelLine={false}
                animationDuration={900}
                label={({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
                  const radius = innerRadius + (outerRadius - innerRadius) / 2;
                  const RADIAN = Math.PI / 180;
                  const x = cx + radius * Math.cos(-(midAngle || 1) * RADIAN);
                  const y = cy + radius * Math.sin(-(midAngle || 1) * RADIAN);

                  return (
                    <text
                      x={x}
                      y={y}
                      fill="#fff"
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={14}
                      fontWeight={600}
                    >
                      {`${chartData[index].value}%`}
                    </text>
                  );
                }}
              >
                {chartData.map((entry: any) => (
                  <Cell key={entry.id} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainerStyled>
        )}
      </Grid>
    </Grid>
  );

  const noDataRender = (
    <ChartNoDataGridItem container size={{ xs: 12 }}>
      <Typography variant="body2">{t(noDataText)}</Typography>
    </ChartNoDataGridItem>
  );

  return (
    <DashboardChartWrapper>
      <Typography variant="subtitle1" sx={{ textAlign: 'center', marginBottom: 2 }}>
        {t('dashboard.chart.ancillaries.label')}
      </Typography>
      {noData ? noDataRender : chartRender}
      <Box
        sx={{
          ...(!isMobile ? { padding: '20px 10px 0px 10px' } : { padding: '5px 10px 0 10px' }),
        }}
      >
        <Button
          variant={EButtonVariants.outlined}
          fullWidth
          label={t('dashboard.chart.ancillaries.seeAncillaries')}
          onClick={() => navigate(ERouteLinks.Ancillaries)}
          size={EButtonSizes.small}
        />
      </Box>
    </DashboardChartWrapper>
  );
};

export default AncillariesChart;
